/**
 * useSound Hook - Sound effects and haptic feedback
 */

import { useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectSettings } from '../store/gameSlice';

const AudioContext = window.AudioContext || window.webkitAudioContext;

const SOUNDS = {
  place: { frequency: 440, duration: 0.1, type: 'sine' },
  shift: { frequency: 330, duration: 0.15, type: 'triangle' },
  select: { frequency: 520, duration: 0.05, type: 'sine' },
  win: { frequencies: [523, 659, 784], duration: 0.2, type: 'sine' },
  draw: { frequency: 350, duration: 0.3, type: 'sawtooth' },
  invalid: { frequency: 200, duration: 0.15, type: 'square' },
  turn: { frequency: 480, duration: 0.08, type: 'sine' },
};

export function useSound() {
  const settings = useSelector(selectSettings);
  const audioContextRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    return audioContextRef.current;
  }, []);

  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.3) => {
    if (!settings.soundEnabled) return;

    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [settings.soundEnabled, getAudioContext]);

  const playChord = useCallback((frequencies, duration, type = 'sine') => {
    if (!settings.soundEnabled) return;

    frequencies.forEach((freq, i) => {
      setTimeout(() => {
        playTone(freq, duration, type, 0.2);
      }, i * 100);
    });
  }, [settings.soundEnabled, playTone]);

  const vibrate = useCallback((pattern = 50) => {
    if (!settings.hapticEnabled) return;
    
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  }, [settings.hapticEnabled]);

  const playPlace = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.place;
    playTone(frequency, duration, type);
    vibrate(30);
  }, [playTone, vibrate]);

  const playShift = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.shift;
    playTone(frequency, duration, type);
    vibrate(20);
  }, [playTone, vibrate]);

  const playSelect = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.select;
    playTone(frequency, duration, type);
    vibrate(10);
  }, [playTone, vibrate]);

  const playWin = useCallback(() => {
    const { frequencies, duration, type } = SOUNDS.win;
    playChord(frequencies, duration, type);
    vibrate([50, 50, 50, 50, 100]);
  }, [playChord, vibrate]);

  const playDraw = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.draw;
    playTone(frequency, duration, type, 0.2);
    vibrate([30, 30, 30]);
  }, [playTone, vibrate]);

  const playInvalid = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.invalid;
    playTone(frequency, duration, type, 0.15);
    vibrate([10, 10, 10]);
  }, [playTone, vibrate]);

  const playTurn = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.turn;
    playTone(frequency, duration, type);
  }, [playTone]);

  return {
    playPlace,
    playShift,
    playSelect,
    playWin,
    playDraw,
    playInvalid,
    playTurn,
    isEnabled: settings.soundEnabled,
  };
}

export default useSound;
